import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, Star, MapPin, Calendar, DollarSign, Search, Filter, Phone, Mail, TrendingUp, Award, Clock, ArrowLeft, X } from 'lucide-react'
import { toast } from '../lib/toast'

interface Client {
  id: string
  name: string
  email: string
  phone?: string
  address: string
  totalJobs: number
  totalSpent: number
  averageRating: number
  lastJobDate: string
  nextJobDate?: string
  status: 'active' | 'inactive' | 'new'
  preferredServices: string[]
  notes?: string
  joinDate: string
}

export default function ProviderClients() {
  const navigate = useNavigate()
  const [selectedNotes, setSelectedNotes] = React.useState<{ clientName: string; notes: string } | null>(null)
  
  const handleBackToDashboard = () => {
    navigate('/dashboard')
  }

  const handleShowMoreNotes = (clientName: string, notes: string) => {
    setSelectedNotes({ clientName, notes })
  }

  const handleCloseNotesModal = () => {
    setSelectedNotes(null)
  }

  const [clients] = React.useState<Client[]>([
    {
      id: '1',
      name: 'Sarah Johnson',
      email: 'sarah.j@email.com',
      phone: '(555) 123-4567',
      address: '123 Main St, Downtown Apartment',
      totalJobs: 24,
      totalSpent: 3600,
      averageRating: 4.9,
      lastJobDate: '2024-03-15',
      nextJobDate: '2024-03-19',
      status: 'active',
      preferredServices: ['Standard House Cleaning', 'Deep Kitchen Cleaning'],
      notes: 'Prefers morning appointments, very satisfied with service',
      joinDate: '2023-06-15'
    },
    {
      id: '2',
      name: 'Mike Williams',
      email: 'mike.w@email.com',
      phone: '(555) 234-5678',
      address: '456 Oak Ave, Suburban House',
      totalJobs: 18,
      totalSpent: 2800,
      averageRating: 4.7,
      lastJobDate: '2024-03-10',
      nextJobDate: '2024-03-19',
      status: 'active',
      preferredServices: ['Deep Kitchen Cleaning', 'Carpet Shampooing'],
      notes: 'Has pets, needs extra attention to pet hair',
      joinDate: '2023-08-20'
    },
    {
      id: '3',
      name: 'Emily Davis',
      email: 'emily.d@business.com',
      phone: '(555) 345-6789',
      address: '789 Business Center Blvd',
      totalJobs: 12,
      totalSpent: 3600,
      averageRating: 5.0,
      lastJobDate: '2024-03-18',
      status: 'active',
      preferredServices: ['Office Cleaning'],
      notes: 'Commercial client, needs after-hours service',
      joinDate: '2023-09-10'
    },
    {
      id: '4',
      name: 'Robert Brown',
      email: 'robert.b@email.com',
      phone: '(555) 456-7890',
      address: '321 Residential Complex',
      totalJobs: 8,
      totalSpent: 960,
      averageRating: 4.5,
      lastJobDate: '2024-03-17',
      status: 'active',
      preferredServices: ['Carpet Shampooing'],
      notes: 'Seasonal client, mainly spring cleaning',
      joinDate: '2024-01-15'
    },
    {
      id: '5',
      name: 'Lisa Anderson',
      email: 'lisa.a@email.com',
      address: '555 Elm Street',
      totalJobs: 2,
      totalSpent: 300,
      averageRating: 0,
      lastJobDate: '2024-03-20',
      status: 'new',
      preferredServices: ['Standard House Cleaning'],
      notes: 'New client, first job scheduled',
      joinDate: '2024-03-10'
    }
  ])

  const [searchTerm, setSearchTerm] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState<string>('all')
  const [sortBy, setSortBy] = React.useState<string>('name')

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.address.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || client.status === statusFilter
    return matchesSearch && matchesStatus
  }).sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name)
      case 'totalSpent':
        return b.totalSpent - a.totalSpent
      case 'totalJobs':
        return b.totalJobs - a.totalJobs
      case 'averageRating':
        return b.averageRating - a.averageRating
      case 'lastJob':
        return new Date(b.lastJobDate).getTime() - new Date(a.lastJobDate).getTime()
      default:
        return 0
    }
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200'
      case 'new':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const renderStars = (rating: number) => {
    if (rating === 0) {
      return <span className="text-sm text-gray-500">No ratings yet</span>
    }
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
      />
    ))
  }

  const handleContactClient = (clientId: string, method: 'email' | 'phone') => {
    const client = clients.find(c => c.id === clientId)
    if (client) {
      if (method === 'email') {
        toast.info(`Opening email client for ${client.email}`)
        // In real app, this would open email client
      } else if (method === 'phone' && client.phone) {
        toast.info(`Calling ${client.phone}`)
        // In real app, this would initiate phone call
      }
    }
  }

  const handleScheduleJob = (clientId: string) => {
    const client = clients.find(c => c.id === clientId)
    if (client) {
      toast.success(`Opening scheduler for ${client.name}`)
      // In real app, this would open scheduling modal
    }
  }

  const totalRevenue = clients.reduce((sum, client) => sum + client.totalSpent, 0)
  const activeClients = clients.filter(c => c.status === 'active').length
  const newClients = clients.filter(c => c.status === 'new').length
  const averageClientValue = totalRevenue / clients.length

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      {/* Header */}
      <header className="bg-white dark:bg-dark-card shadow-sm border-b border-gray-200 dark:border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Users className="w-6 h-6 text-blue-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-dark-text-primary">My Clients</h1>
                <p className="text-sm text-gray-500 dark:text-dark-text-secondary">Manage your client relationships</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Client Statistics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-1 md:gap-4 mb-6">
          <div className="bg-white dark:bg-dark-card p-4 rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-dark-text-secondary">Total Clients</p>
                <p className="text-xl font-bold text-blue-600">{clients.length}</p>
              </div>
              <Users className="w-6 h-6 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white dark:bg-dark-card p-4 rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-dark-text-secondary">Active Clients</p>
                <p className="text-xl font-bold text-green-600">{activeClients}</p>
              </div>
              <TrendingUp className="w-6 h-6 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white dark:bg-dark-card p-4 rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-dark-text-secondary">New Clients</p>
                <p className="text-xl font-bold text-purple-600">{newClients}</p>
              </div>
              <Award className="w-6 h-6 text-purple-500" />
            </div>
          </div>
          
          <div className="bg-white dark:bg-dark-card p-4 rounded-lg shadow-sm border border-gray-200 dark:border-dark-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-dark-text-secondary">Avg Client Value</p>
                <p className="text-xl font-bold text-yellow-600">${averageClientValue.toFixed(0)}</p>
              </div>
              <DollarSign className="w-6 h-6 text-yellow-500" />
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search clients by name, email, or address..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400 bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text-primary"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="new">New</option>
              </select>
              
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400 bg-white dark:bg-dark-card text-gray-900 dark:text-dark-text-primary"
              >
                <option value="name">Sort by Name</option>
                <option value="totalSpent">Sort by Revenue</option>
                <option value="totalJobs">Sort by Jobs</option>
                <option value="averageRating">Sort by Rating</option>
                <option value="lastJob">Sort by Last Job</option>
              </select>
            </div>
          </div>
        </div>

        {/* Clients List */}
        <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
              Clients ({filteredClients.length})
            </h2>
          </div>
          
          <div className="p-6">
            {filteredClients.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text-primary mb-2">No clients found</h3>
                <p className="text-gray-500 dark:text-dark-text-muted">
                  {searchTerm || statusFilter !== 'all' 
                    ? 'Try adjusting your search or filter criteria' 
                    : 'You haven\'t added any clients yet'}
                </p>
                {searchTerm || statusFilter !== 'all' ? (
                  <button
                    onClick={() => {
                      setSearchTerm('')
                      setStatusFilter('all')
                    }}
                    className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Clear Filters
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="mt-4 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Find New Clients
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-1 md:gap-6">
                {filteredClients.map((client) => (
                <div key={client.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 md:p-6 hover:shadow-md transition-shadow flex flex-col h-full min-h-[400px] md:min-h-[500px] overflow-hidden">
                  {/* Client Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary text-lg">{client.name}</h3>
                      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(client.status)} mt-1`}>
                        {client.status.charAt(0).toUpperCase() + client.status.slice(1)}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-green-600">${client.totalSpent}</p>
                      <p className="text-xs text-gray-500 dark:text-dark-text-muted">{client.totalJobs} jobs</p>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-dark-text-secondary">
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{client.email}</span>
                    </div>
                    {client.phone && (
                      <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-dark-text-secondary">
                        <Phone className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{client.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-dark-text-secondary">
                      <MapPin className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{client.address}</span>
                    </div>
                  </div>

                  {/* Rating */}
                  <div className="mb-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600 dark:text-dark-text-secondary">Rating:</span>
                      <div className="flex items-center space-x-1">
                        {renderStars(client.averageRating)}
                      </div>
                    </div>
                  </div>

                  {/* Preferred Services */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 dark:text-dark-text-secondary mb-1">Preferred Services:</p>
                    <div className="flex flex-wrap gap-1">
                      {client.preferredServices.map((service, index) => (
                        <span key={index} className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs rounded">
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Job Dates */}
                  <div className="mb-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-dark-text-secondary">Last Job:</span>
                      <span className="text-gray-900 dark:text-dark-text-primary">{new Date(client.lastJobDate).toLocaleDateString()}</span>
                    </div>
                    {client.nextJobDate && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-dark-text-secondary">Next Job:</span>
                        <span className="text-green-600">{new Date(client.nextJobDate).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>

                  {/* Notes */}
                  {client.notes && (
                    <div className="mb-4 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-sm text-blue-800 dark:text-blue-300">
                      <p className="font-medium">Notes:</p>
                      <p className="line-clamp-3">{client.notes}</p>
                      {client.notes?.length > 150 && (
                        <button
                          onClick={() => handleShowMoreNotes(client.name, client.notes || '')}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-xs font-medium mt-1 flex items-center"
                        >
                          more...
                        </button>
                      )}
                    </div>
                  )}

                  {/* Actions - Always at bottom */}
                  <div className="flex space-x-2 mt-auto pt-4 border-t border-gray-200 dark:border-gray-600">
                    <button
                      onClick={() => handleContactClient(client.id, 'email')}
                      className="flex-1 bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700 transition-colors flex items-center justify-center"
                    >
                      <Mail className="w-4 h-4 mr-1" />
                      Email
                    </button>
                    {client.phone && (
                      <button
                        onClick={() => handleContactClient(client.id, 'phone')}
                        className="flex-1 bg-green-600 text-white px-3 py-2 rounded text-sm hover:bg-green-700 transition-colors flex items-center justify-center"
                      >
                        <Phone className="w-4 h-4 mr-1" />
                        Call
                      </button>
                    )}
                    <button
                      onClick={() => handleScheduleJob(client.id)}
                      className="flex-1 bg-purple-600 text-white px-3 py-2 rounded text-sm hover:bg-purple-700 transition-colors flex items-center justify-center"
                    >
                      <Calendar className="w-4 h-4 mr-1" />
                      Schedule
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        </div>
      </main>

      {/* Notes Modal */}
      {selectedNotes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-card rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-dark-border">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
                Notes - {selectedNotes.clientName}
              </h3>
              <button
                onClick={handleCloseNotesModal}
                className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">
                  {selectedNotes.notes}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-dark-border rounded-b-xl">
              <button
                onClick={handleCloseNotesModal}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
